import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import SEOHead from '@/components/feature/SEOHead';
import { teamMembers, milestones, faqs } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9phjepn2trhb35gvfig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '17c.cc每日大赛',
      url: 'https://17c.cc',
      description: '17c.cc每日大赛是领先的在线每日竞技平台，覆盖六大竞技品类，拥有超过200万注册玩家。',
      foundingDate: '2023',
      founder: { '@type': 'Person', name: '张晨' },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'support@17c.cc',
        availableLanguage: 'Chinese',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于17c.cc每日大赛',
      description: '了解17c.cc每日大赛的品牌故事、发展历程、团队介绍和联系方式。',
      url: 'https://17c.cc/about',
      isPartOf: { '@type': 'WebSite', name: '17c.cc每日大赛', url: 'https://17c.cc' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.slice(0, 4).map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 17c.cc每日大赛 | 品牌介绍与联系方式"
        description="了解17c.cc每日大赛——领先的在线每日竞技平台。品牌故事、发展历程、核心团队、信誉展示及联系方式。覆盖六大竞技品类，200万+注册玩家信赖之选。"
        keywords="关于17c.cc,每日大赛品牌,竞技平台介绍,17c.cc团队,联系方式"
        canonicalUrl="https://17c.cc/about"
        schema={schema}
      />
      <Navbar />
      <BackToTop />

      <main className="pt-16 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto pt-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Hero */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20esports%20company%20headquarters%20with%20warm%20lighting%2C%20open%20workspace%20design%2C%20professional%20atmosphere%2C%20golden%20hour%20interior%2C%20team%20collaboration%20space&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="关于17c.cc每日大赛"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white">
                关于<span className="text-accent-400">我们</span>
              </h1>
              <p className="text-white/80 text-sm md:text-base mt-2 max-w-xl">
                了解17c.cc每日大赛背后的故事与团队
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  让每个人都能享受<span className="text-primary-600">竞技的乐趣</span>
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    17c.cc每日大赛诞生于2023年，创始团队由一群热爱竞技游戏的专业人士组成。我们相信，竞技不应该只是职业选手的专属舞台——每一个热爱挑战的人，都应该有机会在公平、专业的平台上展现自己的实力。
                  </p>
                  <p>
                    经过三年多的发展，17c.cc已成长为国内领先的在线每日竞技平台，覆盖射击、策略、MOBA、格斗、竞速、休闲六大竞技品类，日均赛事超过1,000场，累计注册玩家突破200万。
                  </p>
                  <p>
                    我们致力于通过技术创新，为每一位玩家提供公平、刺激、便捷的竞技体验。AI智能匹配系统确保你遇到的每一位对手都旗鼓相当，专业的反作弊机制守护每一场比赛的公平公正。
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="p-4 rounded-lg bg-primary-50 border border-primary-100">
                    <div className="font-heading text-2xl font-bold text-primary-600">3年+</div>
                    <div className="text-sm text-foreground-600 mt-1">稳定运营</div>
                  </div>
                  <div className="p-4 rounded-lg bg-accent-50 border border-accent-100">
                    <div className="font-heading text-2xl font-bold text-accent-600">99.9%</div>
                    <div className="text-sm text-foreground-600 mt-1">服务可用率</div>
                  </div>
                  <div className="p-4 rounded-lg bg-secondary-100 border border-secondary-200">
                    <div className="font-heading text-2xl font-bold text-secondary-700">200万+</div>
                    <div className="text-sm text-foreground-600 mt-1">注册玩家</div>
                  </div>
                  <div className="p-4 rounded-lg bg-background-200/70 border border-background-300/60">
                    <div className="font-heading text-2xl font-bold text-foreground-900">500万+</div>
                    <div className="text-sm text-foreground-600 mt-1">累计奖金(元)</div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20esports%20team%20collaborating%20in%20modern%20office%2C%20warm%20lighting%2C%20enthusiastic%20atmosphere%2C%20multiple%20monitors%20showing%20game%20analytics%2C%20team%20meeting&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="17c.cc团队"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                发展<span className="text-primary-600">历程</span>
              </h2>
              <p className="text-foreground-600 mt-2">从创立到行业标杆，每一步都坚实有力</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 transform md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`relative flex items-start gap-6 md:gap-10 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex-shrink-0">
                      <div className={`bg-background-50 rounded-lg p-5 border border-background-200/70 ${
                        index % 2 === 0 ? 'md:ml-auto md:mr-0' : 'md:mr-auto md:ml-0'
                      } md:max-w-sm`}>
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                          {milestone.year}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{milestone.title}</h3>
                        <p className="text-sm text-foreground-600">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 transform -translate-x-1/2 mt-6 z-10"></div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                核心<span className="text-primary-600">团队</span>
              </h2>
              <p className="text-foreground-600 mt-2">一群热爱竞技、追求卓越的伙伴</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member) => (
                <div key={member.name} className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden text-center p-6">
                  <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mt-0.5 mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  联系<span className="text-primary-600">我们</span>
                </h2>
                <p className="text-foreground-600 mb-8 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们会在24小时内回复您的留言。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-600">support@17c.cc</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">客服热线</div>
                      <div className="text-sm text-foreground-600">400-888-XXXX（工作日 9:00-18:00）</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-200 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-600">上海市浦东新区张江高科技园区</div>
                    </div>
                  </div>
                </div>

                {/* FAQ */}
                <div className="mt-10">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">常见问题</h3>
                  <div className="space-y-2">
                    {faqs.slice(0, 4).map((faq, index) => (
                      <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                        <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                          <span className="font-medium text-sm text-foreground-900 pr-3">{faq.question}</span>
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-base flex-shrink-0"></i>
                        </summary>
                        <div className="px-4 pb-3">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">发送留言</h3>

                {formStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-primary-600 text-2xl"></i>
                    </div>
                    <h4 className="font-heading text-lg font-semibold text-foreground-950 mb-2">留言发送成功！</h4>
                    <p className="text-sm text-foreground-600 mb-6">感谢您的留言，我们会在24小时内回复您。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
                    >
                      发送新留言
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} data-readdy-form>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-900 mb-1.5">姓名 *</label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-900 mb-1.5">邮箱 *</label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-900 mb-1.5">主题</label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-900 mb-1.5">留言内容 *</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                          placeholder="请输入您的留言内容（不超过500个字符）"
                        ></textarea>
                      </div>

                      <div className="honeypot-field">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                          <i className="ri-error-warning-line"></i>
                          {formError}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="w-full px-5 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}